import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';

import { PathURL } from 'src/utils/constants/path';
import useUserProfile from 'src/hooks/Users/useUserProfile';
import useAuthen from 'src/hooks/Authenticate/authen';
import useLanguage from 'src/hooks/Language/useLanguage';
import AvatarCircle from 'src/components/Commons/Avatar';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

function HeaderUserbox() {
  const { signOut } = useAuthen();
  const { profile } = useUserProfile();
  const { language } = useLanguage();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <AvatarCircle displayName={profile.displayName} pathImage={profile.avatar} />
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {profile.displayName} - {profile.code}
            </UserBoxLabel>
            <UserBoxDescription variant="body2">{profile.roleName}</UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <AvatarCircle displayName={profile.displayName} pathImage={profile.avatar} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{profile.displayName}</UserBoxLabel>
            <UserBoxDescription variant="body2">{profile.roleName}</UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <List sx={{ p: 1 }} component="nav">
          <ListItem to={PathURL.profile} component={NavLink}>
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary={language.profilePage.myProfile} />
          </ListItem>
          <ListItem to="/dashboards/messenger" component={NavLink} className="hidden">
            <InboxTwoToneIcon fontSize="small" />
            <ListItemText primary="Messenger" />
          </ListItem>
          <ListItem to="/management/profile/settings" component={NavLink} className="hidden">
            <AccountTreeTwoToneIcon fontSize="small" />
            <ListItemText primary="Account Settings" />
          </ListItem>
        </List>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button fullWidth onClick={(event: any) => signOut(event)}>
            <LockOpenTwoToneIcon sx={{ mr: 1 }} />
            {language.signOut.title}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
